import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useTickets } from 'contexts/TicketsContext';
import { RequestQueryKeysE } from 'declarations/enums/react-query';
import type { SelectOptionT } from 'declarations/types/select';
import { getErrorMessage, getValidationErrorMessage } from 'helpers/apiError';
import { useTicketMarkReadStatus, useTicketMarkUnReadStatus } from 'hooks/api/useTickets';

interface TicketsReadStatusHookProps {
  selectedTickets: string[];
  setSelectedTickets: (value: string[]) => void;
}

interface TicketsReadStatusHookResult {
  errorIsVisible: boolean;
  selectedOption: SelectOptionT;
  readStatusTicketErrorMessage: string | undefined;
  unreadStatusTicketErrorMessage: string | undefined;
  handlePopupOpen: () => void;
  handlePopupClose: () => void;
  handleOptionChange: (newValue: SelectOptionT | null) => SelectOptionT | null;
  isPopupOpen: boolean;
}

function useTicketsReadStatus({
  selectedTickets,
  setSelectedTickets,
}: TicketsReadStatusHookProps): TicketsReadStatusHookResult {
  const queryClient = useQueryClient();
  const [IsPopupOpen, setPopupVisibility] = useState<boolean>(false);
  const customDefaultValue: SelectOptionT = { value: '', label: '' };
  const [selectedOption, setSelectedOption] = useState<SelectOptionT>(customDefaultValue);
  const { selectedTicketId } = useTickets();

  const markStatusAsReadMutation = useTicketMarkReadStatus({
    onSuccess: (): void => {
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTickets]);
      setSelectedTickets([]);
    },
  });

  const markStatusAsUnReadMutation = useTicketMarkUnReadStatus({
    onSuccess: (): void => {
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTickets]);
      setSelectedTickets([]);
    },
  });

  const readStatusTicketErrorMessage = useMemo((): string | undefined => {
    setPopupVisibility(true);
    return getValidationErrorMessage(markStatusAsReadMutation.error);
  }, [markStatusAsReadMutation.error]);

  const unreadStatusTicketErrorMessage = useMemo((): string | undefined => {
    setPopupVisibility(true);
    return getErrorMessage(markStatusAsUnReadMutation.error);
  }, [markStatusAsUnReadMutation.error]);

  const handleOptionSelection = useCallback(
    (option: SelectOptionT): void => {
      if (option.value === 'read') {
        markStatusAsReadMutation.mutate({
          ticketIds: selectedTickets,
        });
      } else if (option.value === 'unread') {
        markStatusAsUnReadMutation.mutate({ ticketIds: selectedTickets });
      }
    },
    [markStatusAsReadMutation, markStatusAsUnReadMutation, selectedTickets]
  );

  const handlePopupOpen = (): void => {
    setPopupVisibility(true);
  };

  const handlePopupClose = (): void => {
    setPopupVisibility(false);
  };

  const handleOptionChange = (newValue: SelectOptionT | null): SelectOptionT | null => {
    setSelectedOption(newValue || customDefaultValue);
    if (newValue !== null) {
      handleOptionSelection(newValue);
    }
    return newValue;
  };

  useEffect((): void => {
    if (selectedTicketId) {
      markStatusAsReadMutation.mutate({
        ticketIds: [selectedTicketId],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTicketId]);

  return {
    errorIsVisible: markStatusAsReadMutation.isError || markStatusAsUnReadMutation.isError,
    selectedOption,
    isPopupOpen: IsPopupOpen,
    readStatusTicketErrorMessage,
    unreadStatusTicketErrorMessage,
    handlePopupOpen,
    handlePopupClose,
    handleOptionChange,
  };
}

export default useTicketsReadStatus;
